<script setup>
import Tabs from "primevue/tabs";
import Badge from "primevue/badge";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import BatchView from "./BatchView.vue";
import VendorsView from "./VendorsView.vue";
import Button from "primevue/button";
import Menu from "primevue/menu";
import TransactionsFullViewPaginated from "./TransactionsFullViewPaginated.vue";
import CustomersView from "./CustomersView.vue";
import AdminApiClientsView from "./AdminApiClientsView.vue";
import AdminUsersRolesView from "./AdminUsersRolesView.vue";
import SftpClientsView from "./SftpClientsView.vue";
import ChangePasswordDialog from "./ChangePasswordDialog.vue";
import VendorReviewView from "./VendorReviewView.vue";
import { useRoute } from 'vue-router';
import { dashboardAPI } from "../service/api.js";
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useCurrentUser } from "../composables/useCurrentUser";
import { useAuth } from "../composables/useAuth";

const { hasRole } = useCurrentUser();
const { logout } = useAuth();

// Role-gated tabs. The UI conditionally renders these; the API enforces
// the actual authorization via RequireRole middleware.
const showApiClientsTab = computed(() => hasRole("api_client_admin"));
const showUsersRolesTab = computed(() => hasRole("role_admin"));

const emptyBatchFilters = () => ({
  id: "",
  customer_code: "",
  employer_code: "",
  source_name: "",
  funding_status: "",
  file_status: "",
  load_date_from: "",
  load_date_to: "",
  processing_date_from: "",
  processing_date_to: "",
  sum_min: "",
  sum_max: "",
});

const emptyVendorFilters = () => ({
  name: "",
  id: "",
  carepay_vendor_id: "",
  city: "",
  state: "",
  email: "",
  payment_type: "",
  batch_payment_preference: "",
  vendor_type: "",
  payment_eligibility_status: "",
});

/** Returns true if s is a valid MM-DD-YYYY date string (avoids sending partial dates to the API). */
function isValidDateString(s) {
  if (!s || typeof s !== "string" || s.length !== 10) return false;
  if (s[2] !== "-" || s[5] !== "-") return false;
  const m = parseInt(s.slice(0, 2), 10);
  const d = parseInt(s.slice(3, 5), 10);
  const y = parseInt(s.slice(6, 10), 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return false;
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

/** Converts MM-DD-YYYY to YYYY-MM-DD for the API. Call only when isValidDateString(s) is true. */
function mmDdYyyyToYyyyMmDd(s) {
  return s.slice(6, 10) + "-" + s.slice(0, 2) + "-" + s.slice(3, 5);
}

/** Returns the filter params that would be sent to the API (only valid/included fields). Used to avoid refetch when nothing effective changed (e.g. typing incomplete date). */
function getEffectiveFilterParams(f) {
  const p = {};
  if (f.id) p.id = f.id;
  if (f.customer_code) p.customer_code = f.customer_code;
  if (f.employer_code) p.employer_code = f.employer_code;
  if (f.source_name) p.source_name = f.source_name;
  if (f.funding_status) p.funding_status = f.funding_status;
  if (f.file_status) p.file_status = f.file_status;
  if (f.load_date_from && isValidDateString(f.load_date_from)) p.load_date_from = mmDdYyyyToYyyyMmDd(f.load_date_from);
  if (f.load_date_to && isValidDateString(f.load_date_to)) p.load_date_to = mmDdYyyyToYyyyMmDd(f.load_date_to);
  if (f.processing_date_from && isValidDateString(f.processing_date_from)) p.processing_date_from = mmDdYyyyToYyyyMmDd(f.processing_date_from);
  if (f.processing_date_to && isValidDateString(f.processing_date_to)) p.processing_date_to = mmDdYyyyToYyyyMmDd(f.processing_date_to);
  const sumMin = parseInt(f.sum_min, 10);
  const sumMax = parseInt(f.sum_max, 10);
  if (f.sum_min !== "" && !isNaN(sumMin)) p.sum_min = sumMin;
  if (f.sum_max !== "" && !isNaN(sumMax)) p.sum_max = sumMax;
  return p;
}

/** Returns the vendor filter params that would be sent to the API (only non-empty keys). */
function getEffectiveVendorFilterParams(f) {
  const p = {};
  if (f.name) p.name = f.name;
  if (f.id) p.id = f.id;
  if (f.carepay_vendor_id) p.carepay_vendor_id = f.carepay_vendor_id;
  if (f.city) p.city = f.city;
  if (f.state) p.state = f.state;
  if (f.email) p.email = f.email;
  if (f.payment_type) p.payment_type = f.payment_type;
  if (f.batch_payment_preference) p.batch_payment_preference = f.batch_payment_preference;
  if (f.vendor_type) p.vendor_type = f.vendor_type;
  if (f.payment_eligibility_status) p.payment_eligibility_status = f.payment_eligibility_status;
  return p;
}

import { useToast } from "primevue/usetoast";
const toast = useToast();

const customers = ref([]);
const vendors = ref([]);
const vendorPagination = ref({
  page: 1,
  pageSize: 50,
  totalItems: 0,
  totalPages: 0,
});
const vendorSortBy = ref('name');
const vendorSortOrder = ref('asc');
const vendorFilters = ref(emptyVendorFilters());
const vendorLoading = ref(false);
let vendorFilterDebounceTimer = null;
const batches = ref([]);
const batchPagination = ref({
  page: 1,
  pageSize: 50,
  totalItems: 0,
  totalPages: 0,
});
const batchSortBy = ref('load_date');
const batchSortOrder = ref('desc');
const batchFilters = ref(emptyBatchFilters());
const batchLoading = ref(false);
let batchFilterDebounceTimer = null;
const unknownVendorIds = ref([]);
const transactionsComponent = ref(null);
const unknownVendorCount = computed(() => {
  return unknownVendorIds.value.length;
});

const route = useRoute();

// Tab control
const activeTab = ref("0");

// Check URL hash for batch navigation
const checkHashForBatch = () => {
  const batchId = route.params.batchId;

  if (batchId) {
    activeTab.value = "0"; // Switch to Batches tab
    // Trigger scroll after tab switch and data load
    setTimeout(() => {
      const element = document.querySelector(batchId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500);
  }
};

const userMenu = ref(null);
const showChangePasswordDialog = ref(false);
const userMenuItems = [
  {
    label: "Change Password",
    icon: "pi pi-key",
    command: () => {
      showChangePasswordDialog.value = true;
    },
  },
  {
    label: "Logout",
    icon: "pi pi-sign-out",
    command: () => {
      // useAuth.logout clears the token AND useCurrentUser, so role-gated
      // UI re-renders immediately to the login screen — no page reload
      // needed.
      logout();
    },
  },
];

const toggleUserMenu = (event) => {
  userMenu.value.toggle(event);
};

const fetchCustomers = async () => {
  console.log("Fetching Customers...");
  try {
    const response = await dashboardAPI.getCustomers();
    customers.value = response.data || [];
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch customers",
      life: 3000,
    });
    console.error("Error fetching customers:", error);
  }
};

const fetchVendors = async () => {
  vendorLoading.value = true;
  try {
    const params = {
      page: vendorPagination.value.page,
      pageSize: vendorPagination.value.pageSize,
      sortBy: vendorSortBy.value,
      sortOrder: vendorSortOrder.value,
      ...getEffectiveVendorFilterParams(vendorFilters.value),
    };
    const response = await dashboardAPI.getVendors(params);
    const { data, pagination } = response.data || {};
    vendors.value = data || [];
    if (pagination) {
      vendorPagination.value = {
        ...vendorPagination.value,
        ...pagination,
      };
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch vendors",
      life: 3000,
    });
    console.error("Error fetching vendors:", error);
  } finally {
    vendorLoading.value = false;
  }
};

const onVendorPage = (event) => {
  vendorPagination.value.page = event.page + 1;
  vendorPagination.value.pageSize = event.rows;
  fetchVendors();
};

const onVendorSort = (event) => {
  vendorSortBy.value = event.sortField || 'name';
  vendorSortOrder.value = event.sortOrder === 1 ? 'asc' : 'desc';
  vendorPagination.value.page = 1;
  fetchVendors();
};

const onVendorFilter = (newFilters) => {
  const prevEffective = getEffectiveVendorFilterParams(vendorFilters.value);
  vendorFilters.value = { ...emptyVendorFilters(), ...newFilters };
  vendorPagination.value.page = 1;
  const nextEffective = getEffectiveVendorFilterParams(vendorFilters.value);
  if (JSON.stringify(prevEffective) === JSON.stringify(nextEffective)) return;
  if (vendorFilterDebounceTimer) clearTimeout(vendorFilterDebounceTimer);
  vendorFilterDebounceTimer = setTimeout(() => {
    fetchVendors();
    vendorFilterDebounceTimer = null;
  }, 400);
};

const clearVendorFilters = () => {
  vendorFilters.value = emptyVendorFilters();
  vendorPagination.value.page = 1;
  if (vendorFilterDebounceTimer) clearTimeout(vendorFilterDebounceTimer);
  vendorFilterDebounceTimer = null;
  fetchVendors();
};

const fetchUnknownVendorIds = async () => {
  console.log("Fetching Unknown Vendor ids...");
  try {
    const response = await dashboardAPI.getUnkownVendors();
    unknownVendorIds.value = response.data || [];
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch vendors",
      life: 3000,
    });
    console.error("Error fetching vendors:", error);
  }
};

const fetchBatches = async () => {
  console.log("Fetching batches...");
  batchLoading.value = true;
  try {
    const { page, pageSize } = batchPagination.value;
    const params = {
      page,
      pageSize,
      sortBy: batchSortBy.value,
      sortOrder: batchSortOrder.value,
    };
    const f = batchFilters.value;
    if (f.id) params.id = f.id;
    if (f.customer_code) params.customer_code = f.customer_code;
    if (f.employer_code) params.employer_code = f.employer_code;
    if (f.source_name) params.source_name = f.source_name;
    if (f.funding_status) params.funding_status = f.funding_status;
    if (f.file_status) params.file_status = f.file_status;
    if (f.load_date_from && isValidDateString(f.load_date_from)) params.load_date_from = mmDdYyyyToYyyyMmDd(f.load_date_from);
    if (f.load_date_to && isValidDateString(f.load_date_to)) params.load_date_to = mmDdYyyyToYyyyMmDd(f.load_date_to);
    if (f.processing_date_from && isValidDateString(f.processing_date_from)) params.processing_date_from = mmDdYyyyToYyyyMmDd(f.processing_date_from);
    if (f.processing_date_to && isValidDateString(f.processing_date_to)) params.processing_date_to = mmDdYyyyToYyyyMmDd(f.processing_date_to);
    const sumMin = parseInt(f.sum_min, 10);
    const sumMax = parseInt(f.sum_max, 10);
    if (f.sum_min !== "" && !isNaN(sumMin)) params.sum_min = sumMin;
    if (f.sum_max !== "" && !isNaN(sumMax)) params.sum_max = sumMax;
    const response = await dashboardAPI.getBatches(params);
    const { data, pagination } = response.data || {};
    batches.value = data || [];
    if (pagination) {
      batchPagination.value = {
        ...batchPagination.value,
        ...pagination,
      };
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch batches",
      life: 3000,
    });
    console.error("Error fetching batches:", error);
  } finally {
    batchLoading.value = false;
  }
};

const onBatchPage = (event) => {
  batchPagination.value.page = event.page + 1;
  batchPagination.value.pageSize = event.rows;
  fetchBatches();
};

const onBatchSort = (event) => {
  batchSortBy.value = event.sortField || 'load_date';
  batchSortOrder.value = event.sortOrder === 1 ? 'asc' : 'desc';
  batchPagination.value.page = 1;
  fetchBatches();
};

const onBatchFilter = (newFilters) => {
  const prevEffective = getEffectiveFilterParams(batchFilters.value);
  batchFilters.value = { ...emptyBatchFilters(), ...newFilters };
  batchPagination.value.page = 1;
  const nextEffective = getEffectiveFilterParams(batchFilters.value);
  // Only refetch when the params we'd send actually change (avoids flicker while typing e.g. incomplete date)
  if (JSON.stringify(prevEffective) === JSON.stringify(nextEffective)) return;
  if (batchFilterDebounceTimer) clearTimeout(batchFilterDebounceTimer);
  batchFilterDebounceTimer = setTimeout(() => {
    fetchBatches();
    batchFilterDebounceTimer = null;
  }, 400);
};

const clearBatchFilters = () => {
  batchFilters.value = emptyBatchFilters();
  batchPagination.value.page = 1;
  if (batchFilterDebounceTimer) clearTimeout(batchFilterDebounceTimer);
  batchFilterDebounceTimer = null;
  fetchBatches();
};

const errorHandler = (error) => {
  const errorMessage = error.message || error.toString();
  toast.add({
    severity: "error",
    summary: "Error",
    detail: errorMessage,
    life: 3000,
  });
};

const updateData = () => {
  fetchBatches();
  fetchUnknownVendorIds();
  // Refresh transactions if component is mounted
  if (transactionsComponent.value) {
    transactionsComponent.value.refresh();
  }
};

const updateVendors = () => {
  fetchVendors();
  fetchUnknownVendorIds();
};

onMounted(() => {
  fetchCustomers();
  fetchBatches();
  fetchVendors();
  fetchUnknownVendorIds();
  
  // Check hash on initial load
  checkHashForBatch();
  
  // Listen for hash changes
  window.addEventListener('hashchange', checkHashForBatch);
});

onUnmounted(() => {
  // Clean up event listener
  window.removeEventListener('hashchange', checkHashForBatch);
});
</script>

<template>
  <div class="app-container">
    <header class="app-header">
      <div class="header-logo">
        <img src="/logo.png" width="200" height="50" />
      </div>
      <div class="header-right">
        <Button
          icon="pi pi-user"
          class="p-button-text p-button-rounded"
          @click="toggleUserMenu"
          aria-haspopup="true"
          aria-controls="user-menu"
        />
        <Menu
          id="user-menu"
          ref="userMenu"
          :model="userMenuItems"
          :popup="true"
        />
        <ChangePasswordDialog v-model:visible="showChangePasswordDialog" />
      </div>
    </header>

    <div class="app-content">
      <Tabs v-model:value="activeTab">
        <TabList>
          <Tab value="0">Batches</Tab>
          <Tab value="1"
            >Vendors
            <Badge
              v-if="unknownVendorCount > 0"
              severity="danger"
              :value="unknownVendorCount"
          /></Tab>
          <Tab value="2">Transactions</Tab>
          <Tab value="3">Customers</Tab>
          <Tab v-if="showApiClientsTab" value="admin-api-clients">
            <i class="pi pi-key" style="margin-right: 0.5rem" />API clients
          </Tab>
          <Tab v-if="showUsersRolesTab" value="admin-users-roles">
            <i class="pi pi-users" style="margin-right: 0.5rem" />Users &amp; roles
          </Tab>
          <Tab value="sftp-clients">
            <i class="pi pi-server" style="margin-right: 0.5rem" />SFTP Clients
          </Tab>
          <Tab value="vendor-review">
            <i class="pi pi-users" style="margin-right: 0.5rem" />Vendor Review
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="0">
            <BatchView
              @update="updateData"
              @page="onBatchPage"
              @sort="onBatchSort"
              @filter="onBatchFilter"
              @clear-filters="clearBatchFilters"
              :batches="batches"
              :pagination="batchPagination"
              :filter-model="batchFilters"
              :sort-by="batchSortBy"
              :sort-order="batchSortOrder"
              :loading="batchLoading"
            />
          </TabPanel>
          <TabPanel value="1">
            <VendorsView
              @update="updateVendors"
              @page="onVendorPage"
              @sort="onVendorSort"
              @filter="onVendorFilter"
              @clear-filters="clearVendorFilters"
              :vendors="vendors"
              :unknownVendorIds="unknownVendorIds"
              :pagination="vendorPagination"
              :filter-model="vendorFilters"
              :sortBy="vendorSortBy"
              :sortOrder="vendorSortOrder"
              :loading="vendorLoading"
            />
          </TabPanel>
          <TabPanel value="2">
            <TransactionsFullViewPaginated ref="transactionsComponent" />
          </TabPanel>
          <TabPanel value="3">
            <CustomersView
              @error="errorHandler"
              @update="updateData"
              :customers="customers"
            />
          </TabPanel>
          <TabPanel v-if="showApiClientsTab" value="admin-api-clients">
            <AdminApiClientsView />
          </TabPanel>
          <TabPanel v-if="showUsersRolesTab" value="admin-users-roles">
            <AdminUsersRolesView />
          </TabPanel>
          <TabPanel value="sftp-clients">
            <SftpClientsView :customers="customers" />
          </TabPanel>
          <TabPanel value="vendor-review">
            <VendorReviewView />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </div>
</template>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem;
  height: 60px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-logo {
  font-size: 1.5rem;
  font-weight: bold;
}

.app-content {
  flex: 1;
  padding: 1rem;
  overflow: auto;
}
</style>
